import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  OverflowTooltip,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from '@tonic-one/react';
import React from 'react';
import isEqual from 'lodash/isEqual';

interface Props<DataItemType> {
  data: DataItemType[];
  columns: ColumnDef<DataItemType>[];
  identityKeys?: string[];
}

/**
 * CommonTable component
 * @param {string[]} identityKeys - 标识table某一条数据的唯一性的属性keys, 用于判断table是否需要重新渲染
 * @returns {JSX.Element} 返回组件的 JSX 元素
 */
function CommonTable<
  DataItemType extends {
    [key: string]: any;
  },
>(props: Props<DataItemType>): JSX.Element {
  return <MemoTable {...props} />;
}

const MemoTable = React.memo(
  ({
    data,
    columns,
  }: {
    data: any[];
    columns: ColumnDef<any>[];
    identityKeys?: string[];
  }) => {
    const table = useReactTable({
      data,
      columns,
      defaultColumn: {
        minSize: 40,
      },
      getCoreRowModel: getCoreRowModel(),
    });
    return (
      <Table layout="flexbox">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const styleProps = {
                  minWidth: header.column.columnDef.minSize,
                  width: header.getSize(),
                  ...header.column.columnDef.meta?.customColumnStyle,
                };
                const content = flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                );
                return (
                  <TableHeaderCell
                    key={header.id}
                    height="44px"
                    {...styleProps}
                  >
                    <OverflowTooltip label={content}>{content}</OverflowTooltip>
                  </TableHeaderCell>
                );
              })}
            </TableHeaderRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => {
                const needOverflowTooltip =
                  cell.column.columnDef.meta?.needOverflowTooltip ?? true;
                const styleProps = {
                  minWidth: cell.column.columnDef.minSize,
                  width: cell.column.getSize(),
                  ...cell.column.columnDef.meta?.customColumnStyle,
                };
                const content = flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext()
                );
                return (
                  <TableCell
                    key={cell.id}
                    {...styleProps}
                    minHeight="44px"
                    alignItems="center"
                    display="flex"
                  >
                    {needOverflowTooltip ? (
                      <OverflowTooltip
                        label={content}
                        PopperProps={{ usePortal: true }}
                      >
                        {content}
                      </OverflowTooltip>
                    ) : (
                      content
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  },
  (preProps, curProps) => {
    if (!preProps.identityKeys || preProps.identityKeys.length === 0) {
      return preProps.data.every((preData, index) => {
        const curData = curProps.data[index];
        return isEqual(preData, curData);
      });
    } else {
      return preProps.identityKeys.every((key) => {
        return preProps.data.every((preData, index) => {
          const curData = curProps.data[index];
          return isEqual(preData[key], curData[key]);
        });
      });
    }
  }
);

export default CommonTable;
